import { action, computed, makeObservable, observable } from 'mobx'

import { IRootStore } from 'store/types'
import { getPhoneWithoutPrefix } from 'components/CountryPhoneInput/CountryPhoneInput'
import { ALL_EXCEPT_NUMBERS_REGEXP } from 'shared/constants/regExp'
import { Country } from 'shared/types/types'

import { countries } from '../../data'
import { CompanyType, CountryItem, PersonalDataStep, TaxCheckStatusType, TaxTypes } from '../../types'
import { CompanyDetailsViewModel } from '../models/CompanyDetailsViewModel'
import { ProfileViewModel } from '../models/ProfileViewModel'
import { ValidatePersonalDataStore } from '../ValidatePersonalDataStore'
import { ProfileApiStore } from './ProfileApiStore'

export class ProfileUiStore {
  @observable profile = new ProfileViewModel()
  @observable companyDetails = new CompanyDetailsViewModel()
  @observable apiStore = new ProfileApiStore(this)
  @observable loading: boolean = false
  @observable selectedCountry: CountryItem = countries[0].value

  @observable phone: string = ''
  @observable phonePrefix: string = '+7'
  @observable country: Country = Country.RU

  @observable personalDataStep: PersonalDataStep = PersonalDataStep.step1
  @observable readonly validateStore = new ValidatePersonalDataStore(this.profile, this.companyDetails)

  @observable loadingPassportData: boolean = false
  @observable isSendingData: boolean = false

  constructor(readonly rootStore: IRootStore) {
    makeObservable(this)
  }

  get isUserWasEdit() {
    const { snils, additionalPhoneModel, telegramLogin } = this.profile
    const { fullPhone: additionalPhone } = additionalPhoneModel
    return (
      Number(additionalPhone) !== this.user.additional_phone ||
      telegramLogin !== this.user.telegram_login ||
      snils !== String(this.user.snils === 0 ? '' : this.user.snils)
    )
  }

  get user() {
    return this.rootStore.appStore.user.info
  }

  get isOwner() {
    return this.appStore.hasEntity
  }

  @computed
  get appStore() {
    return this.rootStore.appStore
  }

  @computed
  get fullPhone() {
    return `${this.phonePrefix}${this.phone}`
  }

  setSendingData = (value: boolean) => {
    this.isSendingData = value
  }

  @action
  setLoadingPassportData = (value: boolean) => {
    this.loadingPassportData = value
  }

  @action
  setPersonalDataStep = (value: PersonalDataStep) => {
    this.personalDataStep = value
  }

  @action
  setSelectedCountry = (value: CountryItem) => {
    this.selectedCountry = value
    this.phonePrefix = value.value
    this.country = value.country
  }

  @action
  setPhonePrefix = (value: string) => {
    this.phonePrefix = value
  }

  @action
  setCountry = (value: Country) => {
    this.country = value
  }

  @action
  setPhone = (value: string) => {
    this.phone = value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
  }

  @action
  createEditData() {
    const company = this.appStore.company

    this.companyDetails.setSelectedCountry(
      countries.find((item) => item.value.country === company?.country)?.value ?? countries[0].value,
    )
    this.profile.setLastName(this.user.last_name)
    this.profile.setFirstName(this.user.first_name)
    this.profile.setMiddleName(this.user.middle_name)

    this.profile.setSnils(String(this.user.snils !== 0 ? this.user.snils : '')) // С сервера приходит ноль, если пустое значение
    this.profile.setTelegramLogin(this.user.telegram_login)

    this.profile.setAdditionPhone(String(this.user.additional_phone))
    this.profile.setAdditionPhoneCountry(this.user.additional_phone_country)
    this.profile.setEmail(company?.email ?? '')

    this.companyDetails.setBik(company?.bik ?? '')
    this.companyDetails.setInn(company?.inn ?? '')
    this.companyDetails.setPayAccount(company?.payAccount ?? '')
    this.companyDetails.setTaxType(company?.taxType ?? TaxTypes.usn)
    this.companyDetails.setTaxFiles(company?.taxFiles ?? [])
    this.companyDetails.setTaxFilesUI(company?.taxFiles ?? [])
    this.companyDetails.setCompanyType(company?.orgType ?? CompanyType.ip)
    this.companyDetails.setLegalAddress(company?.legalAddress ?? '')
    this.companyDetails.setGln(company?.gln ?? '')

    this.companyDetails.setBankName(company?.bankName ?? '')
    this.companyDetails.setBankInn(company?.bankInn ?? '')
    this.companyDetails.setCorAccount(company?.corAccount ?? '')
    this.companyDetails.setCompanyName(company?.orgName ?? '')
    this.companyDetails.setInnText(company?.inn ?? '')

    this.companyDetails.setTaxCheckStatus(company?.taxCheckStatus ?? TaxCheckStatusType.review)
    this.companyDetails.setTaxCheckStatusComment(company?.taxCheckStatusComment ?? '')

    this.profile.initPhoneData()
    this.initPhoneData()
  }

  @action
  initPhoneData() {
    const countryItem = countries.find((x) => x.value.country === this.user.country) ?? countries[0]
    this.setPhone(getPhoneWithoutPrefix(countryItem.value.country, this.user.phone.toString()))
    this.setSelectedCountry(countryItem.value)
  }

  @action
  setLoading = (value: boolean) => {
    this.loading = value
  }
}
